import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {
  Stack,
  Typography,
  CircularProgress,
  Chip,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import RebelFormLabel from "@/Pages/Hubs/components/FormLabel/RebelFormLabel";
import { isArray } from "lodash";
import { useDispatch } from "react-redux";
import { toast } from "@/components/common/Toast";
import { sleep } from "@/utils/misc";
import { validateEmail } from "@/utils/validation";
import InviteErrorModal from "./InviteErrorModal";

const MultiEmailInvite = ({
  // a default of `undefined` is the same as no default, and it makes the
  // inferred prop type literally `undefined`, rejecting every caller
  title = "",
  placeholder,
  btnText,
  onSubmitRequest,
  onSuccessFn = () => { },
  inputFieldSize = "small",
  isActive = true,
  reverse = false,
  InputProps = {},
  additionalField,
  children,
}) => {
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorEmails, setErrorEmails] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const dispatch = useDispatch();
  const { t } = useTranslation(["translation.Hub"]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { invitees: [] } });

  const invitees = watch("invitees");

  const resetMsgs = () => {
    setShowSuccessMsg(false);
    setShowErrorMsg(false);
  };

  const onSubmit = async (values) => {
    if (!isActive) return;
    resetMsgs();

    let list = [...values.invitees];

    // Commit any partially typed email still in the input
    if (inputValue.trim().length > 0) {
      const trimmed = inputValue.trim();
      if (!validateEmail(trimmed)) {
        setError("invitees", { message: t("invalidEmail") });
        return;
      }
      clearErrors("invitees");
      if (!list.includes(trimmed)) list = [...list, trimmed];
    }

    const onSuccess = async (data) => {
      setValue("invitees", []);
      setInputValue("");
      reset();
      const errorEmailsArr = data.filter((inviteObj) => !!inviteObj.error);
      if (errorEmailsArr.length > 0) {
        setErrorEmails(errorEmailsArr);
        setShowErrorMsg(true);
      } else {
        setShowSuccessMsg(true);
      }
      onSuccessFn();
      await sleep(10000);
      resetMsgs();
    };

    const resp = await onSubmitRequest(list);
    if (resp?.status && resp?.status !== 201) {
      if (typeof resp.data?.error === "string")
        dispatch(toast.error(resp.data.error));
      console.error(resp);
    } else if (resp?.data && isArray(resp.data)) {
      onSuccess(resp.data);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {title && <RebelFormLabel htmlFor="invitees">{title}</RebelFormLabel>}
        <Stack direction={reverse ? "column" : "column-reverse"} sx={{
          gap: 2
        }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              gap: 2,
              alignItems: reverse ? "flex-start" : "flex-end"
            }}>
            <Box
              sx={{
                width: { xs: "100%", sm: "auto", md: "100%", xl: "100%" },
                maxWidth: { xs: "auto", sm: "auto", md: "400px", xl: "600px" },
              }}
            >
              <Controller
                name="invitees"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    disabled={!isActive}
                    options={[]}
                    value={field.value}
                    inputValue={inputValue}
                    onInputChange={(_e, value) => setInputValue(value)}
                    onChange={(_e, newValue) => {
                      // Validate each newly added email
                      const last = newValue[newValue.length - 1];
                      if (last && !validateEmail(last)) return;
                      field.onChange(newValue);
                      setInputValue("");
                    }}
                    renderValue={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={field.value.length === 0 ? placeholder : ""}
                        size={inputFieldSize}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        slotProps={{
                          ...params.slotProps,
                          input: { ...params.slotProps.input, ...InputProps }
                        }}
                      />
                    )}
                  />
                )}
              />
            </Box>
            {additionalField}
            <Button
              data-testid="hub-send-invite-btn"
              variant="contained"
              type="submit"
              size={inputFieldSize === "normal" ? "large" : "small"}
              disabled={
                !isActive ||
                isSubmitting ||
                (invitees.length === 0 && !inputValue.trim())
              }
              sx={{
                minWidth: "max-content",
                width: "max-content",
                margin: { xs: "auto", sm: 0 },
              }}
            >
              {!isSubmitting ? btnText : <CircularProgress size={24.5} />}
            </Button>
            {children}
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              mb: 2,
              flexWrap: "wrap"
            }}>
            {invitees.map((item) => (
              <Chip
                key={item}
                sx={{ borderColor: "transparent", borderRadius: 4 }}
                color="primary"
                variant="outlined"
                label={item}
                onDelete={() => {
                  setValue(
                    "invitees",
                    invitees.filter((i) => i !== item)
                  );
                }}
              />
            ))}
          </Stack>
        </Stack>
        {showSuccessMsg && (
          <Typography
            color="success"
            sx={{ mt: 0.5, fontStyle: "italic" }}
            data-testid="hub-invite-success-response"
          >
            {t("inviteSentMsg")}
          </Typography>
        )}
        {showErrorMsg && (
          <Box data-testid="hub-invite-error-response">
            <Typography color="error" sx={{ mt: 0.5, fontStyle: "italic" }}>
              {`${t("errorSendingInviteMsg")} `}
              <button
                type="button"
                onClick={() => setShowErrorModal(true)}
                style={{
                  textDecoration: "underline",
                  color: "inherit",
                  border: "none",
                  font: "inherit",
                  padding: 0,
                }}
              >
                {t("ClickHere")}
              </button>
            </Typography>
          </Box>
        )}
      </form>
      <InviteErrorModal
        isOpen={showErrorModal}
        close={() => setShowErrorModal(false)}
        t={t}
        errorEmails={errorEmails}
      />
    </Box>
  );
};

export default React.memo(MultiEmailInvite);
